import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { actionTypes } from './appTypes';
import { getSubscribedMsiVesselImageDataError, getSubscribedMsiVesselImageDataSuccess, getSubscribedMsiVesselsError, getSubscribedMsiVesselsSuccess, getUserInfoError, getUserInfoSuccess as getUserInfoSuccess } from './appSlice';
import { TSignedInUser } from './auth/authTypes';
import { getSignedInUserInfo } from './auth/authApi';
import { TSubscribedMsiVessel, TVesselImageData } from './subscription/subscriptionTypes';
import { getSubscribedMsiVesselImageDataApi, getSubscribedMsiVesselsApi } from './subscription/subscriptionApi';
import { ISubscriptionFormData, IVesselFormData } from '../shared/sharedTypes';
import { EOutputType, ESelectionType, ESubscriptionType } from '../shared/sharedEnums';
import { setSelectedVesselsForOutputRequest } from '../features/search/searchSlice';
import { selectVesselImageData } from './appSelectors';
import { isEmpty } from 'lodash';
import { trackEvent, trackExceptionWithContext } from '../services/application-insights/appInsights';
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from '../services/application-insights/appInsightsConstants';
import { setCookie } from '../utils/helpers';

function* getUserByTokenSaga() {
	const sagaId = `user-saga-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		// User info fetch is tracked at API layer (authApi.ts)
		const response: TSignedInUser = yield call(getSignedInUserInfo);
		yield put(getUserInfoSuccess(response));
		
		// Add MSILoggedInEmailAddress cookie for login info on main website
		setCookie('MSILoggedInEmailAddress', response.emailAddress);


		const subscriptions = response.subscriptions.filter(k=> k.type === ESubscriptionType.Seascape);
		if(isEmpty(subscriptions)){
			const error = new Error('No active Seascape subscription found. Please contact MSI.');
			trackExceptionWithContext(error, {
				component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
				action: 'no-seascape-subscription',
				additionalData: {
					errorType: AppInsightsErrorTypes.SUBSCRIPTION_ERROR,
					userId: response.id.toString(),
					totalSubscriptions: response.subscriptions.length,
					subscriptionTypes: response.subscriptions.map(s => s.type).join(','),
					sagaId
				}
			});
			throw error;
		}

		// Subscription data fetch is tracked at API layer (subscriptionApi.ts)
		//Get the user's subscribed vessels by first subscription
		const subscribedMsiVessels: TSubscribedMsiVessel[] = yield call(getSubscribedMsiVesselsApi, response.subscriptions.filter(k=> k.type === ESubscriptionType.Seascape)[0].sectors.map((s) => s.sectorId));

		yield put(getSubscribedMsiVesselsSuccess({ subscribedMsiVessels }));

		const outputType = subscribedMsiVessels.length === 1 ? EOutputType.Vessel : subscribedMsiVessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet;
		
		// Track business logic decision about output type selection
		trackEvent(AppInsightsActions.SUBSCRIPTION_DATA_FETCH_SUCCESS, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'vessels-output-selection-configured',
			vesselCount: subscribedMsiVessels.length,
			outputType,
			sagaId
		});

		yield put(setSelectedVesselsForOutputRequest({
			outputSelection: {
				selectedVesselsList: subscribedMsiVessels.map((v) => ({ name: v.vesselName, imoNumber: v.imoNumber, sectorName: v.sectorName, companyName: v.owner })) as IVesselFormData[],
				outputType,
				selectionType: ESelectionType.None, selectionName: ''
			}
		}));

	} catch (error) {
		trackExceptionWithContext(error as Error, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'get-user-by-token-saga-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.USER_AUTHENTICATION_ERROR,
				errorMessage: (error as Error).message,
				sagaId
			}
		});
		yield put(getUserInfoError(error));
	}
}


export function* getSubscribedMsiVesselsSaga(action: PayloadAction<{ selectedSubscriptionData: ISubscriptionFormData }>) {
	const sagaId = `vessels-saga-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		// Subscription data fetch is tracked at API layer (subscriptionApi.ts)
		const subscribedMsiVessels: TSubscribedMsiVessel[] = yield call(getSubscribedMsiVesselsApi, action.payload.selectedSubscriptionData.subscribedSectorIdList);
		yield put(getSubscribedMsiVesselsSuccess({ subscribedMsiVessels }));

		const outputType = subscribedMsiVessels.length === 1 ? EOutputType.Vessel : subscribedMsiVessels.length > 200 ? EOutputType.Summary : EOutputType.Fleet;
		
		// Track business logic decision about output type selection
		trackEvent(AppInsightsActions.SUBSCRIPTION_DATA_FETCH_SUCCESS, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'subscription-vessels-output-configured',
			vesselCount: subscribedMsiVessels.length,
			outputType,
			subscriptionId: action.payload.selectedSubscriptionData.id.toString(),
			sagaId
		});

		yield put(setSelectedVesselsForOutputRequest({
			outputSelection: {
				selectedVesselsList: subscribedMsiVessels.map((v) => ({ name: v.vesselName, imoNumber: v.imoNumber, sectorName: v.sectorName, companyName: v.owner })) as IVesselFormData[],
				outputType,
				selectionType: ESelectionType.None, selectionName: ''
			}
		}));
	} catch (error: any) {
		trackExceptionWithContext(error, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'get-subscribed-vessels-saga-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.SUBSCRIPTION_ERROR,
				errorMessage: error.message,
				subscriptionId: action.payload.selectedSubscriptionData.id.toString(),
				sectorCount: action.payload.selectedSubscriptionData.subscribedSectorIdList.length,
				sagaId
			}
		});
		yield put(getSubscribedMsiVesselsError({ message: error.message }));
	}
}

export function* getSubscribedMsiVesselImageDataSaga(action: PayloadAction<{ sectorCode: string, imoNumber: number }>) {
	const sagaId = `image-saga-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(AppInsightsActions.VESSEL_IMAGE_FETCH_ATTEMPT, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'get-vessel-image-saga-start',
			sectorCode: action.payload.sectorCode,
			imoNumber: action.payload.imoNumber.toString(),
			sagaId
		});

		let vesselImageData: TVesselImageData = yield select(selectVesselImageData, action.payload.imoNumber);
		
		if (vesselImageData === undefined) {
			let sectorCode = action.payload.sectorCode;
			const originalSectorCode = sectorCode;
			
			if (sectorCode === "LNG" || sectorCode === "LPG")
				sectorCode = "LNG_LPG";
			else if (sectorCode === "T" || sectorCode === "CH")
				sectorCode = "T_CH";

			vesselImageData = yield call(getSubscribedMsiVesselImageDataApi, sectorCode, action.payload.imoNumber);
			
			if (vesselImageData !== undefined) {
				trackEvent(AppInsightsActions.VESSEL_IMAGE_FETCH_SUCCESS, {
					component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
					action: 'vessel-image-api-success',
					imoNumber: action.payload.imoNumber.toString(),
					originalSectorCode,
					mappedSectorCode: sectorCode,
					hasImageData: !!vesselImageData.imageData,
					sagaId
				});

				yield put(getSubscribedMsiVesselImageDataSuccess({ vesselImageData: { imoNumber: action.payload.imoNumber, sourceUrl: vesselImageData.sourceUrl, name: vesselImageData.name, imageData: vesselImageData.imageData } }));
			}
			else {
				trackEvent(AppInsightsActions.VESSEL_IMAGE_FETCH_FAILURE, {
					component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
					action: 'vessel-image-not-found',
					imoNumber: action.payload.imoNumber.toString(),
					sectorCode,
					sagaId
				});

				vesselImageData = { imoNumber: action.payload.imoNumber, sourceUrl: '', name: 'no_vessel_image', imageData: '' };
				yield put(getSubscribedMsiVesselImageDataSuccess({ vesselImageData }));
			}
		} else {
			trackEvent(AppInsightsActions.VESSEL_IMAGE_FETCH_SUCCESS, {
				component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
				action: 'vessel-image-cache-hit',
				imoNumber: action.payload.imoNumber.toString(),
				sectorCode: action.payload.sectorCode,
				sagaId
			});
		}

	} catch (error) { 
		trackExceptionWithContext(error as Error, {
			component: AppInsightsComponents.USER_MANAGEMENT_SAGA,
			action: 'get-vessel-image-saga-error',
			additionalData: {
				errorType: AppInsightsErrorTypes.VESSEL_DATA_ERROR,
				imoNumber: action.payload.imoNumber.toString(),
				sectorCode: action.payload.sectorCode,
				errorMessage: (error as Error).message,
				sagaId
			}
		});

		const vesselImageData = { imoNumber: action.payload.imoNumber, sourceUrl: '', name: 'no_vessel_image', imageData: '' };
		yield put(getSubscribedMsiVesselImageDataError({ vesselImageData }));
	}
}

// Sign out user saga
// function* signOutUserSaga() {
// 	try {
// 		yield call(signOutUser);
// 		yield put(signOutSuccess());
// 	} catch (error) {
// 		yield put(signOutError());
// 	}
// }



export function* watchAppActions(): Generator {
	yield takeLatest(actionTypes.GET_USER_BY_TOKEN_REQUEST, getUserByTokenSaga);
	yield takeLatest(actionTypes.GET_SUBSCRIBED_MSI_VESSELS_REQUEST, getSubscribedMsiVesselsSaga);
	yield takeEvery(actionTypes.GET_SUBSCRIBED_MSI_VESSEL_IMAGE_DATA_REQUEST, getSubscribedMsiVesselImageDataSaga); // takeEvery: parallel fetches per vessel imoNumber
	// yield takeEvery(actionTypes.SIGN_OUT_REQUEST, signOutUserSaga);
}